import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Clock, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import CrossSellPopup from './CrossSellPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const ProductCard = ({ peptide }) => {
  const { addToCart } = useCart();
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(true);
  const [crossSellProduct, setCrossSellProduct] = useState(null);
  const [showCrossSell, setShowCrossSell] = useState(false);
  const isOutOfStock = peptide.out_of_stock;
  const isComingSoon = peptide.coming_soon;
  const isDisabled = isOutOfStock || isComingSoon;
  const hasVariant = !!peptide.no_logo_image;
  const isSnap8 = peptide.id === 'snap-8';

  useEffect(() => {
    if (isSnap8) {
      fetch(`${API}/peptides/hyaluronic-acid-serum`)
        .then(r => r.json())
        .then(d => setCrossSellProduct(d))
        .catch(() => {});
    }
  }, [isSnap8]);

  const triggerCrossSell = () => {
    if (isSnap8 && crossSellProduct && !crossSellProduct.out_of_stock) {
      setShowCrossSell(true);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    if (hasVariant) {
      setShowVariantPicker(true);
      return;
    }
    addToCart(peptide);
    toast.success(`${peptide.name} added to cart`);
    triggerCrossSell();
  };

  const handleToggleVariant = (e, withLogo) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedLogo(withLogo);
  };

  const handleConfirmVariant = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const label = selectedLogo ? 'With Logo' : 'Without Logo';
    const image = selectedLogo ? peptide.image : peptide.no_logo_image;
    addToCart(peptide, { label, image });
    toast.success(`${peptide.name} (${label}) added to cart`);
    setShowVariantPicker(false);
    setSelectedLogo(true);
  };

  const displayImage = showVariantPicker && !selectedLogo && peptide.no_logo_image
    ? peptide.no_logo_image
    : peptide.image;

  return (
    <div className="product-card rounded-sm overflow-hidden group" data-testid={`product-card-${peptide.id}`}>
      <Link to={`/product/${peptide.id}`} className="block">
        {/* Image Container */}
        <div className="relative overflow-hidden">
          {peptide.image ? (
            <img
              src={displayImage}
              alt={peptide.name}
              className={`w-full h-auto block transition-transform duration-500 group-hover:scale-110 ${isDisabled ? 'opacity-50' : ''}`}
            />
          ) : (
            <div className="w-full aspect-square bg-[#121212] flex items-center justify-center">
              <div className="text-center">
                <Clock size={48} className="text-[#D4AF37] mx-auto mb-3" />
                <p className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Coming Soon</p>
              </div>
            </div>
          )}
          {isOutOfStock && (
            <span className="absolute top-3 left-3 bg-red-700 text-white text-xs uppercase tracking-wider px-3 py-1 rounded-sm font-bold" data-testid={`out-of-stock-${peptide.id}`}>
              Out of Stock
            </span>
          )}
          {isComingSoon && peptide.image && (
            <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-xs uppercase tracking-wider px-3 py-1 rounded-sm font-bold" data-testid={`coming-soon-${peptide.id}`}>
              Coming Soon
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-4 pb-2">
          <h3 className="font-['Oswald'] text-lg uppercase tracking-wide text-white mb-1 group-hover:text-[#C41E3A] transition-colors">
            {peptide.name}
          </h3>
          <div className="flex justify-between items-center mb-2">
            <p className="text-[#D4AF37] text-sm font-semibold">{isComingSoon ? 'Coming Soon' : peptide.dosage}</p>
            {!isComingSoon && (
              <div className="flex items-center gap-2">
                {peptide.original_price && <span className="text-gray-500 text-sm line-through">{peptide.original_price}</span>}
                <p className="text-[#C41E3A] text-lg font-bold" data-testid={`price-${peptide.id}`}>{peptide.price}</p>
              </div>
            )}
          </div>
          <p className="text-gray-400 text-xs line-clamp-2 mb-3">{peptide.description}</p>
        </div>
      </Link>

      {/* Action Buttons */}
      <div className="px-4 pb-4">
        {showVariantPicker ? (
          <div className="space-y-2" data-testid={`variant-picker-${peptide.id}`}>
            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold text-center">Select Style</p>
            <div className="flex gap-2">
              <button
                onClick={(e) => handleToggleVariant(e, true)}
                className={`flex-1 text-white text-xs font-semibold uppercase tracking-wider py-2 rounded-sm transition-all duration-200 ${selectedLogo ? 'bg-[#C41E3A]' : 'border border-[#27272A] hover:border-[#C41E3A]'}`}
                data-testid={`variant-with-logo-${peptide.id}`}
              >
                With Logo
              </button>
              <button
                onClick={(e) => handleToggleVariant(e, false)}
                className={`flex-1 text-white text-xs font-semibold uppercase tracking-wider py-2 rounded-sm transition-all duration-200 ${!selectedLogo ? 'bg-[#C41E3A]' : 'border border-[#27272A] hover:border-[#C41E3A]'}`}
                data-testid={`variant-without-logo-${peptide.id}`}
              >
                Without Logo
              </button>
            </div>
            <button
              onClick={handleConfirmVariant}
              className="w-full flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-semibold uppercase tracking-wider py-2 rounded-sm transition-all duration-200"
              data-testid={`confirm-variant-${peptide.id}`}
            >
              <Check size={14} /> Add to Cart
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleAddToCart}
              disabled={isDisabled}
              className={`flex-1 flex items-center justify-center gap-2 text-white text-xs font-semibold uppercase tracking-wider py-2 rounded-sm transition-all duration-300 ${isDisabled ? 'bg-gray-600 cursor-not-allowed' : 'bg-[#C41E3A] hover:bg-[#A01830]'}`}
              data-testid={`add-to-cart-${peptide.id}`}
            >
              {isComingSoon ? (
                <><Clock size={14} /> Coming Soon</>
              ) : isOutOfStock ? (
                <><ShoppingCart size={14} /> Out of Stock</>
              ) : (
                <><ShoppingCart size={14} /> Add to Cart</>
              )}
            </button>
            <Link
              to={`/product/${peptide.id}`}
              className="flex items-center justify-center gap-1 border border-[#27272A] hover:border-[#C41E3A] text-gray-400 hover:text-[#C41E3A] text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-sm transition-all duration-300"
              data-testid={`view-details-${peptide.id}`}
            >
              Details
              <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
      {showCrossSell && crossSellProduct && (
        <CrossSellPopup product={crossSellProduct} onClose={() => setShowCrossSell(false)} />
      )}
    </div>
  );
};

export default ProductCard;
